// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseStatus } from '~/components/common';
// Others
import { IQuoteDetail } from '~/utils/interface/quotes';
import { BusinessTypeEnum, ConnectionStatusEnum, DateFormatEnum, StatusEnum } from '~/utils/enum/common';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { RingCentralContext } from '~/context';
import { formatCurrencyUSD, formatDate, formatPhoneNumber, formatTitleCase, getFullName } from '~/utils/helper/common';
// Styles, images, icons
import styles from './InformationQuote.module.scss';
import { icons } from '~/assets';

type Props = {
  detailQuote?: IQuoteDetail;
};

const cx = classNames.bind(styles);

const InformationQuote = (props: Props) => {
  //#region Destructuring Props
  const { detailQuote } = props;
  const { ringCentralStatus, executeCall } = useContext(RingCentralContext);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleClickPhoneNumber = () => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) {
      alert(t('Ringcentral is not connected'));
      return;
    }

    if (detailQuote?.client?.clientContacts?.[0]?.phoneNumber && detailQuote?.client?.clientId) {
      executeCall &&
        executeCall(detailQuote?.client?.clientContacts?.[0]?.phoneNumber, detailQuote?.id, BusinessTypeEnum.ORDER);
    }
  };

  const renderItem = (title: string, children: React.ReactNode) => {
    return (
      <Stack className={cx('item')}>
        <Typography className={cx('label')}>{title}</Typography>

        <Box>{children}</Box>
      </Stack>
    );
  };
  //#endregion Handle Function

  return (
    <Grid2 container spacing={4} className={cx('container')}>
      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPlusPrimary}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('quote_detail_title_transaction')}</Typography>
          </Stack>

          {renderItem(
            t('quote_detail_title_availability'),
            <Typography className={cx('value')}>
              {detailQuote?.isOverbooking ? (
                <BaseStatus label={StatusEnum.OVERBOOKING} />
              ) : (
                <BaseStatus label={StatusEnum.QUOTE_OPEN} />
              )}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title'),
            <Typography className={cx('value')}>
              <Typography className={cx('value')}>{detailQuote?.id ? `#${detailQuote?.id}` : EMPTY_VALUE}</Typography>
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_status'),
            <>{detailQuote?.status ? <BaseStatus label={detailQuote?.status} /> : EMPTY_VALUE}</>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPayment}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('quote_detail_title_payment')}</Typography>
          </Stack>

          {renderItem(
            t('quote_detail_title_order_total'),
            <Typography className={cx('value')}>
              {formatCurrencyUSD({ value: detailQuote?.total, showDecimal: true }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_amount_paid'),
            <Typography className={cx('value')}>
              {formatCurrencyUSD({
                value: detailQuote?.amountPaid,
                showDecimal: true,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_amount_due'),
            <Typography className={cx('textPrice')}>
              {formatCurrencyUSD({ value: detailQuote?.amountDue, showDecimal: true }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_status_payment'),
            <BaseStatus label={detailQuote?.paymentStatus || EMPTY_STRING} />
          )}

          {renderItem(
            t('quote_detail_title_payment_terms'),
            <Typography className={cx('paymentTerms')}>
              {detailQuote?.paymentTerms ? formatTitleCase(detailQuote?.paymentTerms) : EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconClockTime}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('quote_detail_title_order_date')}</Typography>
          </Stack>

          {renderItem(
            t('quote_detail_title_start_date'),
            <Typography className={cx('value')}>
              {formatDate(detailQuote?.startDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_end_date'),
            <Typography className={cx('value')}>
              {formatDate(detailQuote?.endDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_event_date'),
            <Typography className={cx('value')}>
              {formatDate(detailQuote?.startDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_managed_by'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailQuote?.managedBy?.firstName,
                lastName: detailQuote?.managedBy?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_sales_person'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailQuote?.salesPerson?.firstName,
                lastName: detailQuote?.salesPerson?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={3}>
        <Stack gap={2} className={cx('viewBox')}>
          <Stack gap={2} mb={2} direction={'row'} alignItems={'center'}>
            <img
              src={icons.commonIconPerson}
              alt={t('common_img_text_alt', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('title')}>{t('quote_detail_title_client_overall_info')}</Typography>
          </Stack>

          {renderItem(
            t('quote_detail_title_client_name'),
            <Typography className={cx('value')}>
              {getFullName({
                firstName: detailQuote?.client?.firstName,
                lastName: detailQuote?.client?.lastName,
              }) || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_address'),
            <Typography className={cx('value')}>{detailQuote?.clientAddress?.address || EMPTY_VALUE}</Typography>
          )}

          {renderItem(
            t('quote_detail_title_email'),
            <Typography className={cx('value')}>
              {detailQuote?.client?.clientContacts?.[0]?.email || EMPTY_VALUE}
            </Typography>
          )}

          {renderItem(
            t('quote_detail_title_phone'),
            <Typography className={cx('value', 'phoneNumber')} onClick={handleClickPhoneNumber}>
              {formatPhoneNumber(detailQuote?.client?.clientContacts?.[0]?.phoneNumber || EMPTY_STRING) || EMPTY_VALUE}
            </Typography>
          )}
        </Stack>
      </Grid2>
    </Grid2>
  );
};

export default InformationQuote;
